import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Die wiederkehrenden Bausteine der Oberflaeche an einer Stelle.
 *
 * Grund: sobald Kunden, Objekte, Checklisten und Berichte jeweils eigene
 * Eingabefelder mitbringen, driften Abstaende, Farben und Fehlerdarstellung
 * auseinander. Hier steht das Aussehen einmal, jede Seite benutzt es.
 * Kein Bauteil setzt eigene Farbwerte, die stehen in brand.css.
 */

/*
 * Eingabefelder und Auswahlen sind mindestens 44 Pixel hoch, wie die Knoepfe.
 * Am Telefon entscheidet diese Zahl darueber, ob ein Feld beim ersten Tippen
 * getroffen wird. Textfelder wachsen ohnehin ueber ihre Zeilenzahl hinaus.
 */
const feldRahmen =
  "min-h-11 w-full rounded border border-linie/40 bg-white px-3 py-2 outline-none focus:border-marke disabled:bg-grund/60";

/**
 * Ein Foto aufnehmen oder aussuchen.
 *
 * Der Anlass kam aus dem Betrieb: im Tankbuch stand nur das Wort "Beleg" und
 * darunter der nackte Dateiknopf des Browsers, beschriftet mit "Choose File".
 * Auf Englisch, klein, und ohne jeden Hinweis darauf, dass hier ein Foto vom
 * Kassenzettel hingehoert. Ein Mitarbeiter an der Zapfsaeule liest das nicht
 * als Aufforderung, und dann bleibt der Beleg eben aus.
 *
 * Deshalb ein richtiger Knopf in der Sprache des Mitarbeiters, und danach das
 * Bild selbst statt eines Dateinamens: nur so sieht er, ob der Beleg lesbar
 * getroffen ist oder ob der Daumen im Weg war.
 *
 * capture="environment" oeffnet am Telefon direkt die Kamera. Am Rechner
 * bleibt es die gewohnte Dateiauswahl, denn dort gibt es nichts zu knipsen.
 */
export function Fotofeld({
  datei, onDatei, pflicht = false, beschriftung
}: {
  datei: File | null;
  onDatei: (datei: File | null) => void;
  /** Zeigt an, dass ohne Foto nicht gespeichert werden kann. */
  pflicht?: boolean;
  /**
   * Was auf dem Knopf steht, solange kein Foto da ist.
   *
   * "Foto" allein reicht dort nicht, wo nicht aus dem Zusammenhang hervorgeht,
   * wovon. Am Beleg heisst der Knopf deshalb "Beleg fotografieren".
   */
  beschriftung?: string;
}) {
  const { t } = useTranslation();
  const feld = useRef<HTMLInputElement>(null);
  const [vorschau, setVorschau] = useState<string | null>(null);

  /*
   * Die Vorschau haengt an einer URL, die der Browser fuer die Datei anlegt.
   * Sie muss wieder freigegeben werden, sonst haelt eine Maske, die zehnmal
   * geoeffnet wird, zehn Fotos im Speicher.
   */
  useEffect(() => {
    if (!datei) { setVorschau(null); return; }
    const url = URL.createObjectURL(datei);
    setVorschau(url);
    return () => URL.revokeObjectURL(url);
  }, [datei]);

  return (
    <div>
      <input
        ref={feld}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          onDatei(e.target.files?.[0] ?? null);
          // Zuruecksetzen, damit dasselbe Foto ein zweites Mal ausgewaehlt
          // werden kann. Ohne diese Zeile passiert beim zweiten Mal nichts.
          e.target.value = "";
        }}
      />
      {vorschau && (
        <img src={vorschau} alt="" className="mb-2 max-h-48 rounded border border-linie" />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Knopf art="neben" onClick={() => feld.current?.click()}>
          {datei ? t("foto.anderes") : (beschriftung ?? t("foto.aufnehmen"))}
        </Knopf>
        {datei && (
          <Textknopf gefahr onClick={() => onDatei(null)}>{t("foto.entfernen")}</Textknopf>
        )}
        {!datei && pflicht && (
          <span className="text-xs text-red-700">{t("foto.belegPflicht")}</span>
        )}
      </div>
    </div>
  );
}

export function Karte({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-card bg-white p-5 shadow-sm ${className}`}>{children}</div>;
}

export function Seitenkopf({
  titel,
  hinweis,
  aktionen
}: {
  titel: string;
  hinweis?: string;
  aktionen?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold">{titel}</h1>
        {hinweis && <p className="mt-1 text-sm text-leise">{hinweis}</p>}
      </div>
      {aktionen && <div className="flex flex-wrap items-center gap-2">{aktionen}</div>}
    </div>
  );
}

/**
 * Der Knopf.
 *
 * Zwei Festlegungen, die am Telefon den Unterschied machen:
 *
 * **Mindestens 44 Pixel hoch.** Das ist die Groesse, die eine Fingerkuppe
 * sicher trifft; darunter faengt das Danebentippen an. Vorher waren es
 * sechsunddreissig, und getippt wird diese Anwendung im Stehen, mit
 * Arbeitshandschuhen, auf einem Hof in der Sonne.
 *
 * **breit** macht ihn ueber die volle Breite. Gedacht fuer schmale Geraete,
 * wo ein Knopf in der Zeile mitschwimmt und dabei klein wird.
 */
export function Knopf({
  children,
  art = "haupt",
  breit = false,
  ...rest
}: {
  children: ReactNode;
  art?: "haupt" | "neben" | "warnung";
  breit?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const stil =
    art === "haupt"
      ? "bg-marke text-white hover:bg-marke-dunkel"
      : art === "warnung"
        ? "border border-red-700/40 text-red-700 hover:bg-red-50"
        : "border border-linie/40 text-tinte hover:bg-grund";
  return (
    <button
      {...rest}
      className={`inline-flex min-h-11 items-center justify-center rounded px-4 py-2 text-sm font-semibold transition disabled:opacity-60 ${breit ? "w-full" : ""} ${stil}`}
    >
      {children}
    </button>
  );
}

/**
 * Der Knopf, der wie ein Link aussieht.
 *
 * Fuer Nebenhandlungen: loeschen, abmelden, aufklappen. Er traegt keine
 * Flaeche, aber eine Trefferflaeche: mindestens 44 Pixel hoch, so wie jeder
 * andere Knopf auch.
 *
 * Vorher waren das nackte <button>-Elemente mit `text-xs underline`, sechzehn
 * Pixel hoch. Am Wochenbrett standen neun davon untereinander, jeder loescht
 * einen Termin, und jeder war mit dem Finger kaum zu treffen. Wer daneben
 * tippt, loescht den Termin daneben.
 */
export function Textknopf({
  children,
  gefahr = false,
  ...rest
}: { children: ReactNode; gefahr?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={`inline-flex min-h-11 items-center text-xs underline transition disabled:opacity-50 ${
        gefahr ? "text-red-700 hover:text-red-800" : "text-leise hover:text-tinte"
      }`}
    >
      {children}
    </button>
  );
}

export function Feld({
  label,
  hinweis,
  breit,
  children
}: {
  label: string;
  hinweis?: string;
  breit?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`block ${breit ? "sm:col-span-2" : ""}`}>
      <span className="mb-1 block text-sm font-medium">{label}</span>
      {children}
      {hinweis && <span className="mt-1 block text-xs text-leise">{hinweis}</span>}
    </label>
  );
}

export function Eingabe(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={feldRahmen} />;
}

/**
 * Ein Passwortfeld mit Auge.
 *
 * Punkte statt Zeichen schuetzen gegen den Blick ueber die Schulter. Auf dem
 * Telefon steht aber niemand hinter dem Mitarbeiter, dafuer trifft der Daumen
 * auf Glas das Y statt des X, und das faellt erst beim dritten Fehlversuch
 * auf. Beim Vergeben eines neuen Passworts ist es schlimmer: dort steht der
 * Vertipper zweimal, und danach kommt man mit einem Passwort nicht mehr
 * herein, das man selbst nie gesehen hat.
 *
 * Sichtbar ist es nur, solange jemand den Knopf ausdruecklich gedrueckt hat,
 * und jedes Feld entscheidet das fuer sich.
 *
 * Kein Symbol von aussen, sondern gezeichnete Pfade: was erst nachgeladen
 * wird, fehlt genau dann, wenn das Netz schlecht ist.
 */
export function Passwortfeld({
  wert, onWert, autoComplete, zeigen, verbergen,
  required = true, minLength, className = ""
}: {
  wert: string;
  onWert: (wert: string) => void;
  autoComplete: "current-password" | "new-password";
  /** Beschriftung des Knopfes, solange das Passwort verdeckt ist. */
  zeigen: string;
  /** Und wenn es sichtbar ist. */
  verbergen: string;
  required?: boolean;
  minLength?: number;
  className?: string;
}) {
  const [sichtbar, setSichtbar] = useState(false);
  return (
    <div className={`relative ${className}`}>
      <input
        type={sichtbar ? "text" : "password"}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        value={wert}
        onChange={(e) => onWert(e.target.value)}
        // Platz fuer den Knopf am Ende der Zeile, nicht rechts: auf Arabisch
        // steht die Maske spiegelverkehrt und soll ohne Sonderfall stimmen.
        className={`${feldRahmen} pe-12`}
      />
      <button
        type="button"
        onClick={() => setSichtbar((s) => !s)}
        aria-pressed={sichtbar}
        aria-label={sichtbar ? verbergen : zeigen}
        title={sichtbar ? verbergen : zeigen}
        className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-leise hover:text-tinte"
      >
        <Auge offen={sichtbar} />
      </button>
    </div>
  );
}

/** Das Auge, durchgestrichen wenn das Passwort gerade sichtbar ist. */
function Auge({ offen }: { offen: boolean }) {
  return (
    <svg
      width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3.2" />
      {offen && <path d="M3 21 21 3" />}
    </svg>
  );
}

export function Auswahl(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={feldRahmen} />;
}

export function Textfeld(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} rows={props.rows ?? 4} className={feldRahmen} />;
}

export function Fehler({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className="mb-4 rounded border border-red-700/30 bg-red-50 px-3 py-2 text-sm text-red-800">
      {text}
    </p>
  );
}

export function Leer({ text }: { text: string }) {
  return <p className="rounded-card bg-white p-8 text-center text-leise">{text}</p>;
}

export function Laedt() {
  return <p className="p-8 text-center text-leise">…</p>;
}

/** Kennzeichnet archivierte Eintraege, damit sie nicht mit aktiven verwechselt werden. */
export function ArchivMarke({ text }: { text: string }) {
  return (
    <span className="rounded bg-leise/20 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-leise">
      {text}
    </span>
  );
}
